import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api from '@/api';
import { FormFieldLabel, FormInput } from '@/common/forms/FormInput';
import { InfoSectionContainer } from '@/common/InfoSection';
import { LargeArrowBackButton } from '@/common/LargeArrowBackButton';
import { WBSButton } from '@/common/WBSButton';
import {
  PEOPLE_PICKER_DEBOUNCE_MS,
  PEOPLE_PICKER_MIN_QUERY,
  PEOPLE_PICKER_PAGE_SIZE,
  type Person,
  useAsyncPeoplePicker,
} from '~/hooks/useAsyncPeoplePicker';
import { SearchSelect } from '~/styles/forms/SearchSelect';

export type MemberTagFormValues = {
  tagName: string;
  memberIds: string[];
};

type MemberTagFormProps = {
  groupId: number;
  submitLabel: string;
  initialTagName?: string;
  initialMembers?: Person[];
  isSubmitting?: boolean;
  onSubmit: (values: MemberTagFormValues) => void;
  onBack: () => void;
};

export function MemberTagForm({
  groupId,
  submitLabel,
  initialTagName = '',
  initialMembers,
  isSubmitting = false,
  onSubmit,
  onBack,
}: MemberTagFormProps) {
  const { t } = useTranslation();
  const [tagName, setTagName] = useState(initialTagName);
  // What the person is typing, and what the query is actually keyed on. Legacy
  // debounces the same way (`select2 { ajax: { delay: 250 } }`); without it
  // every keystroke past the third is its own request, because the term is part
  // of the query key.
  const [term, setTerm] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setSearch(term), PEOPLE_PICKER_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [term]);

  // A server search, not a client filter over a preloaded roster: the largest
  // tag-bearing group derives 160 students, so anything unpaginated truncates.
  // The population is the group's derived students, which is also exactly what
  // a tag's member list renders -- legacy's picker additionally offers admins
  // and mods that its member list then hides, so tagging a coordinator there
  // creates an invisible, unremovable row.
  //
  // This is `get_group_students`, so on a group type with no Students tab
  // (social, untyped) it answers 403 and the picker stays empty. That is the
  // interaction between the Students-tab type gate and Member Tagging having no
  // type gate of its own; no such group owns a tag today.
  const { data: matches, isFetching } = api.getGroupStudents.useQuery(
    {
      path: { group_id: groupId },
      query: { search, limit: PEOPLE_PICKER_PAGE_SIZE },
    },
    {
      enabled: groupId > 0 && search.length >= PEOPLE_PICKER_MIN_QUERY,
    },
  );

  const results = useMemo<Person[]>(
    () =>
      (matches?.data ?? []).map((student) => ({
        id: student.id,
        name: student.display_name,
      })),
    [matches],
  );

  // The control stays in its loading state through the debounce window too, so
  // it does not flash "no results" between a keystroke and the request it
  // caused.
  const isSearching = term.length >= PEOPLE_PICKER_MIN_QUERY;
  const membersPicker = useAsyncPeoplePicker(
    {
      results,
      loading: isSearching && (term !== search || isFetching),
      onSearch: setTerm,
    },
    true,
  );
  const { preselect } = membersPicker;

  useEffect(() => {
    if (!initialMembers) return;
    for (const m of initialMembers) {
      preselect(m);
    }
  }, [initialMembers, preselect]);

  return (
    <InfoBox>
      <LargeArrowBackButton onClick={onBack} />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit({ tagName, memberIds: membersPicker.value });
        }}
      >
        <FormInput
          alternate
          required
          hideRequiredLabel
          inputStyle={{ backgroundColor: 'var(--grey-75)' }}
          label={t('wbs.tag-name')}
          value={tagName}
          onChange={setTagName}
        />
        <Field>
          <FormFieldLabel>{t('profile.members')}</FormFieldLabel>
          <SearchSelect
            multi
            loading={membersPicker.loading}
            minQueryLength={PEOPLE_PICKER_MIN_QUERY}
            options={membersPicker.options}
            value={membersPicker.value}
            onChange={membersPicker.onChange}
            onSearch={membersPicker.onSearch}
          />
        </Field>
        <Footer>
          <WBSButton type="submit" wide isLoading={isSubmitting}>
            {submitLabel}
          </WBSButton>
        </Footer>
      </form>
    </InfoBox>
  );
}

const InfoBox = styled(InfoSectionContainer)`
  position: relative;
`;

const Field = styled.div`
  margin-bottom: 24px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;
`;
